import {
  cloneElement,
  ComponentRef,
  isValidElement,
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { TimePicker as AntTimePicker } from 'antd';
import type { TimePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNativeInputEvents } from '../../../hooks/useNativeInputEvents';
import { takeOverKey, usePickerPopupKeyboard } from '../../../hooks/usePickerPopupKeyboard';
import { focusPickerControl, getPickerFooterStops } from '../../../helpers/pickerPopupHelper';

const StyledTimePicker = styled(AntTimePicker)`
  .ant-picker-suffix {
    display: none;
  }
` as typeof AntTimePicker;

/** Seconds are below the granularity every picker here is formatted to. */
const currentMinute = () => dayjs().second(0).millisecond(0);

const HOUR_IN_MINUTES = 60;
/** rc-picker confirms typed text itself when the field closes; a commit this soon after ours is that echo. */
const DUPLICATE_WINDOW_MS = 200;

const TIME_CELL_ATTRIBUTE = 'data-picker-time-cell';
const TIME_COLUMN = 'ul.ant-picker-time-panel-column';
const TIME_ROW = 'li.ant-picker-time-panel-cell';
const DISABLED_ROW = 'ant-picker-time-panel-cell-disabled';
const SELECTED_ROW = 'ant-picker-time-panel-cell-selected';

const cellOf = (row: Element | null | undefined) =>
  row?.querySelector<HTMLElement>(`[${TIME_CELL_ATTRIBUTE}]`) ?? null;

const enabledRows = (column: Element) =>
  Array.from(column.querySelectorAll<HTMLElement>(TIME_ROW)).filter(
    (row) => !row.classList.contains(DISABLED_ROW),
  );

/** A column's tab stop: its selected value, or its first one when nothing is selected. */
const activeCellOf = (column: Element) =>
  cellOf(column.querySelector(`${TIME_ROW}.${SELECTED_ROW}:not(.${DISABLED_ROW})`)) ??
  cellOf(enabledRows(column)[0]);

const timeColumns = (popup: HTMLElement) => Array.from(popup.querySelectorAll(TIME_COLUMN));

const rowAfterKey = (row: HTMLElement, key: string) => {
  const rows = enabledRows(row.parentElement ?? row);
  const index = rows.indexOf(row);
  if (key === 'ArrowUp') return rows[index - 1];
  if (key === 'ArrowDown') return rows[index + 1];
  if (key === 'Home') return rows[0];
  if (key === 'End') return rows[rows.length - 1];
  return undefined;
};

/** The tab stop of the column beside the cell's own, on the side the arrow points to. */
const cellInNeighbouringColumn = (popup: HTMLElement, cell: HTMLElement, key: string) => {
  const columns = timeColumns(popup);
  const index = columns.findIndex((column) => column.contains(cell));
  const column = columns[index + (key === 'ArrowLeft' ? -1 : 1)];
  return column ? activeCellOf(column) : null;
};

const enabledOkButton = (popup: HTMLElement) => {
  const ok = popup.querySelector<HTMLButtonElement>('.ant-picker-ok button');
  return ok?.disabled ? null : ok;
};

/**
 * An empty picker highlights midnight, so confirming with OK lands on 00:00 unless
 * the user scrolls both columns all the way to the time they meant. `defaultOpenValue`
 * moves the highlight — and what OK commits — to the current time without writing a
 * value into the field. It is read on every open so a form left sitting for an hour
 * still offers the time it is now rather than the time it was mounted.
 *
 * In the field, ArrowUp and ArrowDown change the time by a minute (an hour with Shift, never
 * past the day's edges), and a time typed into the field is committed when the field is left.
 * Alt+ArrowDown, Space, or Enter on an unedited field open the columns with focus in them.
 */
export const TimePicker = ({
  defaultOpenValue,
  onOpenChange,
  onChange,
  value,
  format,
  disabledTime,
  open: openProp,
  ...restProps
}: TimePickerProps) => {
  const { t } = useTranslation();
  const pickerRef = useRef<ComponentRef<typeof AntTimePicker>>(null);
  const lastTypedCommit = useRef<{ time: Dayjs; at: number } | null>(null);
  const [lastOpenedAt, setLastOpenedAt] = useState(currentMinute);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = openProp ?? internalOpen;

  const applyOpen = (open: boolean) => {
    if (open) {
      setLastOpenedAt(currentMinute());
    }
    setInternalOpen(open);
    onOpenChange?.(open);
  };

  const timeFormat = typeof format === 'string' ? format : 'HH:mm';
  const emitChange = (time: Dayjs) => onChange?.(time, time.format(timeFormat));

  const handleChange: TimePickerProps['onChange'] = (time, timeString) => {
    const lastTyped = lastTypedCommit.current;
    const echoesTypedCommit =
      time &&
      lastTyped?.time.isSame(time, 'minute') &&
      Date.now() - lastTyped.at < DUPLICATE_WINDOW_MS;
    if (echoesTypedCommit) return;
    onChange?.(time, timeString);
  };

  const isDisabledHour = (time: Dayjs) =>
    !!disabledTime?.(time)?.disabledHours?.().includes(time.hour());

  const stepTime = (event: KeyboardEvent) => {
    const isArrow = event.key === 'ArrowUp' || event.key === 'ArrowDown';
    if (!isArrow || event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();

    const base = value ?? lastOpenedAt;
    const minutes = event.shiftKey ? HOUR_IN_MINUTES : 1;
    const next = base.add(event.key === 'ArrowUp' ? minutes : -minutes, 'minute');
    if (!next.isSame(base, 'day') || isDisabledHour(next)) return;
    emitChange(next);
  };

  const commitTypedTime = (input: HTMLInputElement) => {
    const typedText = input.value.trim();
    if (!typedText) return;
    const typed = dayjs(typedText, timeFormat, true);
    if (!typed.isValid()) return;
    const time = (value ?? dayjs()).hour(typed.hour()).minute(typed.minute()).second(0);
    if (value?.isSame(time, 'minute')) return;
    if (isDisabledHour(time)) return;
    lastTypedCommit.current = { time, at: Date.now() };
    emitChange(time);
  };

  const handleControlKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    const popup = event.currentTarget;
    const target = event.target as HTMLElement;
    const cell = target.closest<HTMLElement>(`[${TIME_CELL_ATTRIBUTE}]`);
    if (!cell) {
      if ((event.key === 'Enter' || event.key === ' ') && target instanceof HTMLAnchorElement) {
        takeOverKey(event);
        target.click();
      }
      return;
    }

    const row = cell.closest<HTMLElement>(TIME_ROW);
    if (event.key === 'Enter') {
      const ok = enabledOkButton(popup);
      if (!ok) return;
      takeOverKey(event);
      ok.click();
      return;
    }
    if (event.key === ' ') {
      takeOverKey(event);
      row?.click();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const nextCell = cellInNeighbouringColumn(popup, cell, event.key);
      if (!nextCell) return;
      takeOverKey(event);
      focusPickerControl(nextCell);
      return;
    }

    const nextRow = row && rowAfterKey(row, event.key);
    const nextCell = cellOf(nextRow);
    if (!nextRow || !nextCell) return;
    takeOverKey(event);
    nextRow.click();
    focusPickerControl(nextCell);
  };

  const findFirstColumnCell = (popup: HTMLElement) => {
    const [firstColumn] = timeColumns(popup);
    return firstColumn ? activeCellOf(firstColumn) : null;
  };

  const popupKeyboard = usePickerPopupKeyboard({
    isOpen,
    setOpen: applyOpen,
    label: t('task:timeInputPlaceholder'),
    getInput: () => pickerRef.current?.nativeElement?.querySelector('input'),
    getActiveControl: findFirstColumnCell,
    getTabStops: (popup) =>
      [...timeColumns(popup).map(activeCellOf), ...getPickerFooterStops(popup)].filter(
        (stop): stop is HTMLElement => !!stop,
      ),
    onControlKeyDown: handleControlKeyDown,
  });

  const handleOpenChange = (open: boolean) => {
    popupKeyboard.noteOpenChange(open);
    applyOpen(open);
  };

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) return;
    if (isOpen && event.key === 'Escape') {
      takeOverKey(event);
      applyOpen(false);
      return;
    }
    const input = event.currentTarget as HTMLInputElement;
    const committedText = value ? value.format(timeFormat) : '';
    const opensColumns =
      (event.altKey && event.key === 'ArrowDown') ||
      (!event.altKey && event.key === ' ') ||
      (!event.altKey && !isOpen && event.key === 'Enter' && input.value === committedText);
    if (opensColumns) {
      takeOverKey(event);
      popupKeyboard.focusInside();
      return;
    }
    stepTime(event);
  };

  useNativeInputEvents(pickerRef, { onKeyDown: handleInputKeyDown, onBlur: commitTypedTime });

  const columnNames: Record<string, string> = {
    hour: t('common:hours'),
    minute: t('common:minutes'),
  };

  const renderCell: TimePickerProps['cellRender'] = (current, info) => {
    if (typeof current !== 'number' || !info.subType || !isValidElement(info.originNode)) {
      return info.originNode;
    }
    const valueText = String(current).padStart(2, '0');
    return cloneElement(info.originNode as ReactElement<Record<string, unknown>>, {
      [TIME_CELL_ATTRIBUTE]: info.subType,
      tabIndex: -1,
      role: 'button',
      'aria-label': [columnNames[info.subType], valueText].filter(Boolean).join(' '),
    });
  };

  return (
    <StyledTimePicker
      {...restProps}
      ref={pickerRef}
      value={value}
      format={format}
      disabledTime={disabledTime}
      onChange={handleChange}
      defaultOpenValue={defaultOpenValue ?? lastOpenedAt}
      open={isOpen}
      onOpenChange={handleOpenChange}
      cellRender={renderCell}
      panelRender={popupKeyboard.panelRender}
    />
  );
};
